import { useEffect, useState } from "react"

import { Flex, Form, Input } from "antd"
import { useTranslation } from "react-i18next"

import ButtonComponent from "../../../Components/Button"
import PasswordValidator from "../../../Components/PasswordValidator"

function CreateNewPassword({
  userError,
  saveResult,
  onClose,
  onSubmit,
  userErrorsReset,
  userSaveResultReset,
  password_created
}) {
  const [errorText, setErrorText] = useState(null)

  const [values, setValues] = useState({
    password: null,
    confirm: null
  })

  const [form] = Form.useForm()
  const { t } = useTranslation()

  useEffect(() => {
    if (userError) {
      if (typeof userError === "string") {
        if (userError.length > 100) {
          setErrorText(t("Server response error"))
        } else {
          setErrorText(userError)
        }
      } else {
        setErrorText(Object.values(userError)[0])
      }
    }
  }, [userError])

  useEffect(() => {
    if (saveResult?.create) {
      clearFormData()
      onClose()
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [saveResult, userError])

  function clearFormData() {
    form.resetFields()
    setValues({ password: null, confirm: null })
    setErrorText(null)
    userSaveResultReset()
    userErrorsReset()
  }

  const onFinish = (value) => {
    const data = { ...value }
    userErrorsReset()
    onSubmit(data.password)
  }

  const onFinishFailed = ({ errorFields }) => {
    if (errorFields) {
      setErrorText(errorFields[0].errors)
    }
  }

  function handleClearError(e, field) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
    setErrorText(null)
  }

  function onFocusHandler(e) {
    handleClearError(e, e.target.name)
  }

  function handleChange(e) {
    const { name, value } = e.target
    setValues({ ...values, [name]: value })
  }

  return (
    <Form
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      validateTrigger="onSubmit"
      layout="vertical"
      form={form}
    >
      <Form.Item
        label={t("New password")}
        name="password"
        rules={[
          {
            required: true,
            message: t("Please enter your password!")
          },
          ({ getFieldValue }) => ({
            validator(rule, value) {
              if (!value || !getFieldValue("confirm") || getFieldValue("confirm") === value) {
                if (value?.length < 8) {
                  return Promise.reject(t("Must be at least 8 characters"))
                } else {
                  return Promise.resolve()
                }
              } else {
                return Promise.reject(t("Passwords do not match"))
              }
            }
          })
        ]}
        validateStatus={errorText ? "error" : null}
      >
        <Form.Item name="password" noStyle>
          <Input.Password
            name="password"
            placeholder={t("Enter new password")}
            autoComplete="new-password"
            visibilityToggle
            onChange={handleChange}
            onFocus={onFocusHandler}
          />
        </Form.Item>
      </Form.Item>

      <PasswordValidator value={values.password} error={errorText} />

      <Form.Item
        label={t("Confirm new password")}
        name="confirm"
        validateStatus={errorText ? "error" : null}
        dependencies={["password"]}
        rules={[
          {
            required: true,
            message: t("Please confirm your password!")
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || !getFieldValue("password") || getFieldValue("password") === value) {
                return value?.length < 8
                  ? Promise.reject(t("Must be at least 8 characters"))
                  : Promise.resolve()
              }
              return Promise.reject(t("Passwords do not match"))
            }
          })
        ]}
      >
        <Input.Password
          name="confirm"
          placeholder={t("Confirm new password")}
          autoComplete="dontshow"
          visibilityToggle
          onChange={handleChange}
          onFocus={onFocusHandler}
        />
      </Form.Item>

      <Flex gap={16} justify="end" style={{ padding: "0 3px 3px 0" }}>
        {!password_created && (
          <ButtonComponent size="large" htmlType="button" onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
        )}

        <ButtonComponent size="large" type="primary" htmlType="submit" disabled={!values.confirm}>
          {t(password_created ? "Confirm" : "Continue")}
        </ButtonComponent>
      </Flex>
    </Form>
  )
}

export default CreateNewPassword
